import { Inject, Injectable } from "@angular/core";
import { createEffect } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
	CORE_ROUTE,
	CoreState,
	DepositLimitConfigService,
	DepositLimitRequiredService,
	DialogService,
	MarketSelector,
} from "@obg/ngx.core";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { DataBusFacade, EventBusFacade } from "remote-module-context";
import { combineLatest, EMPTY, of } from "rxjs";
import { isEmpty, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { T_SHELL_DATA_BUS_FACADE, T_SHELL_EVENT_BUS_FACADE } from "../injection";
import { observeEvent } from "../util/observe-event";

@Injectable({
	providedIn: "root",
})
export class DepositLimitEffects {
	required$ = createEffect(
		() =>
			combineLatest([
				this.depositLimitConfig.getIsDepositLimitRequired$(),
				this.depositLimitRequiredService.shouldOpenDepositLimit$(),
			]).pipe(
				tap(([required, shouldOpenDepositLimit]) => {
					if (!required) {
						this.dataBus.dispatch({ key: "depositLimitRequired", value: false });
						return;
					}
					this.dataBus.dispatch({
						key: "depositLimitRequired",
						value: shouldOpenDepositLimit,
					});
				})
			),
		{ dispatch: false }
	);

	navigateToDepositLimit$ = createEffect(() =>
		observeEvent<{ returnUrl: string }>(this.eventBus, "handleDepositLimitRequired").pipe(
			withLatestFrom(
				this.store.select(RouteSelector.getCurrent),
				this.store.select(this.marketSelector.getLanguageCode())
			),
			switchMap(([{ returnUrl }, currentRoute, languageCode]) => {
				if (currentRoute && this.depositLimitConfig.isSgaDepositLimitDialogEnabled()) {
					return this.store.select(RouteSelector.getReferer).pipe(
						map((route) => route?.pathName || `/${languageCode}`),
						take(1),
						switchMap((originUrl) => {
							const open$ =
								this.dialogService.openRoute(CORE_ROUTE.responsibleGaming.depositLimitDialog) ?? EMPTY;

							return open$.pipe(
								isEmpty(),
								take(1),
								map(() => RouteAction.navigateByUrl(originUrl, { queryParams: { returnUrl } }))
							);
						})
					);
				}
				return of(
					RouteAction.navigate(CORE_ROUTE.responsibleGaming.depositLimit, undefined, {
						queryParams: { returnUrl },
					})
				);
			})
		)
	);

	constructor(
		@Inject(T_SHELL_DATA_BUS_FACADE) private dataBus: DataBusFacade,
		@Inject(T_SHELL_EVENT_BUS_FACADE) private eventBus: EventBusFacade,
		private store: Store<CoreState>,
		private depositLimitConfig: DepositLimitConfigService,
		private depositLimitRequiredService: DepositLimitRequiredService,
		private marketSelector: MarketSelector,
		private dialogService: DialogService
	) {}
}
